
import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from "react"
import { apiRequest } from "@/lib/api-client"
import { toast } from "@/hooks/use-toast"
import { useUser } from "./user-context"

interface Gym {
  _id: string
  name: string
  address?: string
  phone?: string
  email?: string
  status?: string
  createdAt?: string
  updatedAt?: string
}

interface GymContextType {
  gyms: Gym[]
  selectedGym: string
  isLoading: boolean
  fetchGyms: () => Promise<Gym[]>
  selectGym: (gymId: string) => void
  createGym: (gymData: Partial<Gym>) => Promise<Gym | null>
}

const GymContext = createContext<GymContextType | undefined>(undefined)

export function GymProvider({ children }: { children: ReactNode }) {
  const [gyms, setGyms] = useState<Gym[]>([])
  const [selectedGym, setSelectedGym] = useState<string>("")
  const [isLoading, setIsLoading] = useState(true)
  const [lastFetchTime, setLastFetchTime] = useState(0)
  const { user, isAuthenticated } = useUser()

  const fetchGyms = useCallback(async () => {
    if (!isAuthenticated) return []

    // Prevent excessive fetching by implementing a cooldown
    const now = Date.now()
    if (now - lastFetchTime < 2000) {
      // 2 second cooldown
      return gyms
    }

    setLastFetchTime(now)
    setIsLoading(true)

    try {
      console.log("Fetching gyms...")
      const response = await apiRequest({
        method: "GET",
        url: "/gyms",
      })

      console.log("Gyms response:", response)

      let fetchedGyms: Gym[] = []

      if (response && response.results) {
        fetchedGyms = response.results
      } else if (response && Array.isArray(response)) {
        fetchedGyms = response
      } else if (response) {
        // Single gym object
        fetchedGyms = [response]
      }

      console.log("Processed gyms:", fetchedGyms)
      setGyms(fetchedGyms)

      // If we have gyms but no selected gym, select the first one
      if (fetchedGyms.length > 0 && !selectedGym) {
        setSelectedGym(fetchedGyms[0]._id)
      }

      return fetchedGyms
    } catch (error) {
      console.error("Failed to fetch gyms:", error)
      toast({
        title: "Error",
        description: "Failed to fetch gyms",
        variant: "destructive",
      })
      return []
    } finally {
      setIsLoading(false)
    }
  }, [isAuthenticated, selectedGym, lastFetchTime, gyms])

  const selectGym = (gymId: string) => {
    setSelectedGym(gymId)
    localStorage.setItem("selectedGymId", gymId)
  }

  const createGym = async (gymData: Partial<Gym>) => {
    setIsLoading(true)
    try {
      const response = await apiRequest({
        method: "POST",
        url: "/gyms",
        data: gymData,
      })

      console.log("Create gym response:", response)

      if (response && response.results) {
        // Refresh the gyms list
        await fetchGyms()

        // Select the newly created gym
        if (response.results._id) {
          selectGym(response.results._id)
        }

        toast({
          title: "Success",
          description: "Gym created successfully",
        })

        return response.results
      }

      return null
    } catch (error) {
      console.error("Failed to create gym:", error)
      toast({
        title: "Error",
        description: "Failed to create gym",
        variant: "destructive",
      })
      return null
    } finally {
      setIsLoading(false)
    }
  }

  // Load gyms when user changes
  useEffect(() => {
    if (user) {
      console.log("User changed, fetching gyms")
      fetchGyms()
    }
  }, [user, fetchGyms])

  // Try to restore selected gym from localStorage
  useEffect(() => {
    const storedGymId = localStorage.getItem("selectedGymId")
    if (storedGymId && gyms.length > 0) {
      const gym = gyms.find((g) => g._id === storedGymId)
      if (gym) {
        setSelectedGym(gym._id)
      } else {
        // If stored gym not found, select the first one
        setSelectedGym(gyms[0]._id)
      }
    } else if (gyms.length > 0) {
      // If no stored gym, select the first one
      setSelectedGym(gyms[0]._id)
    }
  }, [gyms])

  return (
    <GymContext.Provider
      value={{
        gyms,
        selectedGym,
        isLoading,
        fetchGyms,
        selectGym,
        createGym,
      }}
    >
      {children}
    </GymContext.Provider>
  )
}

// Export both function names for backward compatibility
export function useGym() {
  const context = useContext(GymContext)
  if (context === undefined) {
    throw new Error("useGym must be used within a GymProvider")
  }
  return context
}

export function useGymContext() {
  return useGym()
}
